import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Bug, Lightbulb, Loader2, Mic, MicOff } from 'lucide-react';
import AnnotationCanvas from './AnnotationCanvas';
import ReportImageList from './ReportImageList';
import { errorText } from '../lib/apiErrors';
import { REPORT_IMAGE_LIMITS, capturePage, describeCaptureError, markReportOpen, newImageId } from '../lib/reportImages';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// The dialog content carries this marker; capture waits for it to leave the DOM
// (the overlay unmounts with it, both live in one DialogPortal) and
// `isExcludedFromCapture` skips it as a second guard.
const DIALOG_MARKER = 'report-dialog';

const nextFrame = () => new Promise((resolve) => {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 16);
});

async function waitForDialogToLeave(maxMs = 500) {
    const started = Date.now();
    while (document.querySelector(`[data-report-ui="${DIALOG_MARKER}"]`) && Date.now() - started < maxMs) {
        // eslint-disable-next-line no-await-in-loop -- polling the DOM until Radix finishes its exit animation
        await new Promise((resolve) => setTimeout(resolve, 25));
    }
    await nextFrame();
    await nextFrame();
}

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

function VoiceButton({ onTranscript, className = '' }) {
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);

    // The dialog unmounts its content while it steps aside for Capture or
    // Annotate, and on close: stop listening then, or the microphone stays on.
    useEffect(() => () => recognitionRef.current?.abort(), []);

    const toggle = useCallback(() => {
        if (listening) {
            recognitionRef.current?.stop();
            setListening(false);
            return;
        }

        if (!SpeechRecognition) {
            toast.error('Voice input not supported in this browser');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    transcript += event.results[i][0].transcript;
                }
            }
            if (transcript) onTranscript(transcript.trim());
        };

        recognition.onerror = (e) => {
            if (e.error !== 'aborted') toast.error(`Voice error: ${e.error}`);
            setListening(false);
        };

        recognition.onend = () => setListening(false);

        recognitionRef.current = recognition;
        recognition.start();
        setListening(true);
    }, [listening, onTranscript]);

    if (!SpeechRecognition) return null;

    return (
        <button type="button" onClick={toggle} title={listening ? 'Stop recording' : 'Voice input'}
            className={`p-1.5 rounded-lg transition-colors ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700'} ${className}`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}

const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-stone-100 text-stone-600' },
    { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
];

const CATEGORIES = [
    { value: 'ui', label: 'UI' }, { value: 'backend', label: 'Backend' },
    { value: 'performance', label: 'Performance' }, { value: 'integration', label: 'Integration' },
    { value: 'data', label: 'Data' }, { value: 'other', label: 'Other' },
];

/**
 * The reporter dialog, used by the floating Report button (ScreenshotCapture)
 * and by New Request on the Feature Requests page (#932).
 *
 * It holds up to 5 images. Capture page and Annotate both need the dialog out
 * of the way, so it HIDES (Dialog open=false) instead of unmounting: every
 * field, image and voice transcript lives in this component's state and
 * survives. The parent unmounts the whole modal on close, which is what
 * releases the images.
 */
export default function BugReportModal({
    open,
    onClose,
    initialImages = [],
    initialCaptureError = null,
    pageUrl = '',
    showPageUrl = true,
    defaultType,
    description: dialogDescription,
    onSubmitted,
    testId = 'report-dialog',
}) {
    const { getAuthHeader } = useAuth();
    const startsWithImage = initialImages.length > 0;
    const [type, setType] = useState(defaultType || (startsWithImage ? 'bug' : 'feature'));
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState(startsWithImage ? 'high' : 'medium');
    const [category, setCategory] = useState('other');
    const [images, setImages] = useState(initialImages);
    const [captureError, setCaptureError] = useState(initialCaptureError);
    const [mode, setMode] = useState('form'); // form | capturing | annotating
    const [annotatingId, setAnnotatingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // The control to focus when the dialog comes back from Capture or Annotate
    // (its content remounts, and would otherwise focus Title).
    const [returnFocus, setReturnFocus] = useState(null);
    const uid = useId();
    const ids = { category: `${uid}-category`, title: `${uid}-title`, description: `${uid}-description` };

    // Counted as open (the floating Report button hides) while open, including
    // while it steps aside for a capture.
    useEffect(() => (open ? markReportOpen() : undefined), [open]);

    // A file dropped beside the drop zone would make the browser open it in the
    // tab and lose everything typed; only the image area takes drops.
    useEffect(() => {
        if (!open) return undefined;
        const keep = (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); };
        window.addEventListener('dragover', keep);
        window.addEventListener('drop', keep);
        return () => {
            window.removeEventListener('dragover', keep);
            window.removeEventListener('drop', keep);
        };
    }, [open]);

    const focusReturnTarget = (event) => {
        if (!returnFocus) return;
        const target = event.currentTarget?.querySelector?.(returnFocus);
        setReturnFocus(null);
        if (target && !target.disabled) {
            event.preventDefault();
            target.focus();
        }
    };

    const handleSubmit = async () => {
        if (!title.trim() || title.trim().length < 5) {
            toast.error('Title must be at least 5 characters');
            return;
        }
        if (!description.trim() || description.trim().length < 10) {
            toast.error('Description must be at least 10 characters');
            return;
        }

        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/feature-requests/`, {
                title: title.trim(),
                description: description.trim(),
                type,
                priority,
                category,
                page_url: pageUrl || window.location.href,
                attachments: images.map((img) => img.dataUrl),
            }, { headers: getAuthHeader() });

            toast.success(type === 'bug' ? 'Bug report submitted!' : 'Feature request submitted!');
            // #931: the report is filed even when an image could not be stored.
            const failed = Number(res?.data?.attachments_failed) || 0;
            if (failed > 0) {
                const saved = Number(res?.data?.attachments_saved) || 0;
                const what = `${failed} screenshot${failed === 1 ? '' : 's'} could not be saved.`;
                toast.warning(saved > 0
                    ? `${what} The report was filed with the other ${saved}.`
                    : `${what} The report was filed without ${failed === 1 ? 'it' : 'them'}.`);
            }
            setSubmitting(false);
            onSubmitted?.(res?.data);
            onClose();
        } catch (err) {
            // The dialog stays open with everything typed and attached.
            toast.error(errorText(err, 'Failed to submit'));
            setSubmitting(false);
        }
    };

    const handleCapture = async () => {
        setCaptureError(null);
        setReturnFocus('[data-testid="report-capture"]');
        setMode('capturing');
        try {
            await waitForDialogToLeave();
            const dataUrl = await capturePage();
            setImages((prev) => (prev.length >= REPORT_IMAGE_LIMITS.maxCount
                ? prev
                : [...prev, { id: newImageId(), dataUrl, source: 'capture' }]));
        } catch (err) {
            console.error('Screenshot capture failed:', err);
            setCaptureError(describeCaptureError(err));
        } finally {
            setMode('form');
        }
    };

    const annotating = mode === 'annotating' ? images.find((img) => img.id === annotatingId) : null;

    const handleAnnotate = (id) => {
        setAnnotatingId(id);
        setReturnFocus(`[data-annotate-id="${id}"]`);
        setMode('annotating');
    };

    const handleAnnotationDone = (dataUrl) => {
        setImages((prev) => prev.map((img) => (img.id === annotatingId ? { ...img, dataUrl, source: 'annotated' } : img)));
        setAnnotatingId(null);
        setMode('form');
    };

    const handleAnnotationCancel = () => {
        setAnnotatingId(null);
        setMode('form');
    };

    // Hiding the dialog for capture/annotate does not call onOpenChange, so this
    // only fires for the user's own close (X, Esc, click outside).
    const handleOpenChange = (next) => {
        if (!next && mode === 'form' && !submitting) onClose();
    };

    return (
        <>
            <Dialog open={open && mode === 'form'} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[520px] gap-3.5" data-report-ui={DIALOG_MARKER} data-testid={testId}
                    onOpenAutoFocus={focusReturnTarget}
                    {...(dialogDescription ? {} : { 'aria-describedby': undefined })}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {type === 'bug' ? <Bug className="w-5 h-5 text-red-500" /> : <Lightbulb className="w-5 h-5 text-amber-500" />}
                            {type === 'bug' ? 'Report a Bug' : 'Request a Feature'}
                        </DialogTitle>
                        {dialogDescription && <DialogDescription>{dialogDescription}</DialogDescription>}
                    </DialogHeader>

                    <div className="space-y-3.5 py-1">
                        {/* Type toggle */}
                        <div className="bg-stone-100 rounded-lg p-0.5 flex">
                            <button type="button" onClick={() => setType('bug')} aria-pressed={type === 'bug'}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    type === 'bug' ? 'bg-white shadow-sm text-red-600' : 'text-stone-500'
                                }`}>
                                <Bug className="w-3.5 h-3.5" /> Bug Report
                            </button>
                            <button type="button" onClick={() => setType('feature')} aria-pressed={type === 'feature'}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    type === 'feature' ? 'bg-white shadow-sm text-amber-600' : 'text-stone-500'
                                }`}>
                                <Lightbulb className="w-3.5 h-3.5" /> Feature Request
                            </button>
                        </div>

                        {/* Priority + Category */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <span className="block text-xs font-medium text-stone-600 mb-1.5">Priority</span>
                                <div className="flex gap-1">
                                    {PRIORITIES.map(p => (
                                        <button key={p.value} type="button" onClick={() => setPriority(p.value)} aria-pressed={priority === p.value}
                                            className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded-md transition-colors ${
                                                priority === p.value ? p.color + ' ring-1 ring-current' : 'bg-stone-50 text-stone-400'
                                            }`}>
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label htmlFor={ids.category} className="block text-xs font-medium text-stone-600 mb-1.5">Category</label>
                                <select id={ids.category} value={category} onChange={e => setCategory(e.target.value)}
                                    className="w-full px-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Page URL indicator */}
                        {showPageUrl && pageUrl && (
                            <div className="text-[10px] text-stone-400 bg-stone-50 px-2 py-1 rounded">
                                Reporting from: <span className="font-mono">{pageUrl.replace(window.location.origin, '')}</span>
                            </div>
                        )}

                        {/* Title */}
                        <div>
                            <label htmlFor={ids.title} className="block text-xs font-medium text-stone-600 mb-1.5">Title</label>
                            <div className="flex items-center gap-1.5">
                                <input id={ids.title} value={title} onChange={e => setTitle(e.target.value)}
                                    placeholder={type === 'bug' ? 'Brief description of the bug...' : 'What feature would you like?'}
                                    className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    data-testid="request-title-input"
                                    autoFocus={!returnFocus} />
                                <VoiceButton onTranscript={t => setTitle(prev => prev ? `${prev} ${t}` : t)} />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label htmlFor={ids.description} className="text-xs font-medium text-stone-600">Description</label>
                                <VoiceButton onTranscript={t => setDescription(prev => prev ? `${prev} ${t}` : t)} />
                            </div>
                            <textarea id={ids.description} value={description} onChange={e => setDescription(e.target.value)}
                                rows={3}
                                placeholder={type === 'bug' ? 'Steps to reproduce, expected vs actual behavior...' : 'Describe the feature and why it would be useful...'}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                data-testid="request-description-input" />
                        </div>

                        <ReportImageList
                            images={images}
                            onChange={setImages}
                            onCapture={handleCapture}
                            onAnnotate={handleAnnotate}
                            captureError={captureError}
                            capturing={mode === 'capturing'}
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                        <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-request-btn"
                            className={type === 'bug' ? 'bg-red-600 hover:bg-red-700' : ''}>
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {open && mode === 'capturing' && (
                <div data-screenshot-capture="true" role="status"
                    className="fixed bottom-20 right-6 z-50 flex items-center gap-2 h-10 px-4 rounded-full bg-stone-900/90 text-white text-xs shadow-lg">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Capturing this page…
                </div>
            )}

            {open && annotating && (
                <AnnotationCanvas
                    screenshotDataUrl={annotating.dataUrl}
                    onComplete={handleAnnotationDone}
                    onCancel={handleAnnotationCancel}
                />
            )}
        </>
    );
}
